import angular from 'angular'
import { compose } from '../../util'
import { wrapWithErrorBoundary } from './error-boundary'
import { wrapWithI18nBridge } from './i18n-bridge'
import { wrapWithI18nProvider } from './i18n-provider'
import { wrapWithApolloProvider } from '../apollo-wrapper'
import { getProviderWrapper } from '../shared-react-state/custom-context-wrapper'
import { RouteProvider } from '../../components/route-provider'
import { wrapWithReactQuery } from '../react-query-wrapper'
import { wrapWithSplitClient } from '../splitio-wrapper'
import { wrapWithSkeletorLocalizationProvider } from './skeletor-localization-provider'
import { wrapWithSkeletorThemeProvider } from './skeletor-theme-provider'

type NgReactInjectable =
  | string
  | [
      string,
      {
        readonly watchDepth: 'reference' | 'collection' | 'value'
      }
    ]

type NgReactInjectablesGetter = (...args: any[]) => {}

const module = angular.module('contrast.angularReactBridge.module', [])

export function createNgReactBridgeDirective(
  componentName: string,
  dependencyNames: string[],
  reactComponent: React.ComponentType<any>,
  propNames: NgReactInjectable[],
  directiveConfig = {},
  getInjectables: NgReactInjectablesGetter = () => ({})
) {
  return module.directive(componentName, [
    'reactDirective',
    '$rootScope',
    '$translate',
    ...dependencyNames,
    (reactDirective, rootScope, translate, ...dependencies) => {
      return reactDirective(
        compose(
          wrapWithErrorBoundary,
          wrapWithI18nBridge,
          wrapWithI18nProvider,
          wrapWithSkeletorLocalizationProvider,
          wrapWithSkeletorThemeProvider,
          (WrappedComponent) =>
            wrapWithReactQuery(WrappedComponent, rootScope, false),
          (WrappedComponent) =>
            wrapWithSplitClient(WrappedComponent, rootScope),
          wrapWithApolloProvider(rootScope),
          getProviderWrapper(RouteProvider)
        )(reactComponent),
        propNames,
        directiveConfig,
        {
          ...getInjectables(dependencies),
          ngTranslate: { rootScope, translate },
          rootScope
        }
      )
    }
  ]).name
}

export function createNgReactBridgeDirectiveFn(
  directiveName: string,
  directive: any
) {
  return module.directive(directiveName, directive)
}

export function createNgReactBridgeFactory(serviceName: string, service: any) {
  return module.factory(serviceName, service)
}

export function createNgReactBridgeConstant(
  constantName: string,
  value: unknown
) {
  return module.constant(constantName, value)
}

export function createNgReactBridgeController(
  controllerName: string,
  controller: any
) {
  return module.controller(controllerName, controller)
}
